import { useCallback, useEffect, useRef, useState } from "react";

import * as api from "../../lib/api.js";
import type { ChatSegmentOut, ChatThreadOut } from "../contract/api";
import { chatProblemOf, type ChatLimit, type ChatProblem } from "./result-chat-model";

/** An answer being written: the question, and the answer's pieces so far. */
export interface StreamingAnswer {
  question: string;
  /** The model's scope decision, once made; nothing is shown before it. */
  inScope: boolean | null;
  segments: ChatSegmentOut[];
}

export interface MeetingChat {
  /** This person's chat and whether they can ask, once loaded. */
  thread: ChatThreadOut | null;
  loading: boolean;
  problem: ChatProblem | null;
  /** With a plan limit problem (#874), the limit and when Pro's month resets. */
  limit: ChatLimit | null;
  /** The question the last error was for, so Try again can ask it again. */
  failedQuestion: string | null;
  streaming: StreamingAnswer | null;
  /** The browser says it is online. */
  online: boolean;
  /** Read the chat again. */
  load(): void;
  /**
   * Ask a question. Resolves with what stopped it (`null` once it was answered, or when
   * another one is already being answered or the screen left the meeting).
   */
  ask(question: string): Promise<ChatProblem | null>;
  /** Ask the question that failed again. */
  retry(): void;
  /** Delete the chat (also how a full one is started over). True when it went. */
  remove(): Promise<boolean>;
  /** Forget the last problem. */
  dismiss(): void;
}

function isOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

type AskError = {
  aborted?: boolean;
  status?: number;
  code?: string | null;
  retryAfter?: number;
  limit?: number;
  resetsAt?: string;
  plan?: string | null;
};

/**
 * One person's Ask AI chat about a meeting (#791), kept at the screen's level so closing
 * the panel loses nothing: an answer being written keeps streaming while the panel is
 * closed, and is there when it opens again.
 *
 * - **Loaded when `active`** (the result is on screen and the API has its token), so the
 *   screen knows whether to offer Ask AI at all.
 * - **One question at a time**, as the server allows. The answer streams in piece by piece
 *   and is replaced, when it is done, by the saved question and answer the server returns.
 * - **Leaving cancels.** Leaving the screen, or opening another meeting from it, aborts the
 *   request, and the server stops the model. An abandoned answer is not saved.
 * - **Every request carries its meeting**: an answer for a meeting the screen has left is
 *   dropped.
 */
export function useMeetingChat(sessionId: string, active: boolean): MeetingChat {
  const [thread, setThread] = useState<ChatThreadOut | null>(null);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState<ChatProblem | null>(null);
  const [limit, setLimit] = useState<ChatLimit | null>(null);
  const [failedQuestion, setFailedQuestion] = useState<string | null>(null);
  const [streaming, setStreaming] = useState<StreamingAnswer | null>(null);
  const [online, setOnline] = useState(isOnline);
  const [nonce, setNonce] = useState(0);
  const current = useRef(sessionId);
  const controller = useRef<AbortController | null>(null);

  // Another meeting starts clean, and anything still streaming for the last one stops.
  useEffect(() => {
    current.current = sessionId;
    setThread(null);
    setProblem(null);
    setFailedQuestion(null);
    setStreaming(null);
    return () => {
      controller.current?.abort();
      controller.current = null;
    };
  }, [sessionId]);

  useEffect(() => {
    const up = () => {
      setOnline(true);
      setProblem((p) => (p === "offline" ? null : p));
    };
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  useEffect(() => {
    if (!active || !sessionId) return;
    const id = sessionId;
    let alive = true;
    setLoading(true);
    api
      .getChat(id)
      .then(
        (out) => {
          if (!alive || current.current !== id) return;
          setThread(out);
          setProblem((p) => (p === "load_failed" ? null : p));
        },
        () => {
          if (alive && current.current === id) setProblem("load_failed");
        },
      )
      .finally(() => {
        if (alive && current.current === id) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [active, sessionId, nonce]);

  const ask = useCallback(
    async (question: string): Promise<ChatProblem | null> => {
      const id = sessionId;
      const text = question.trim();
      if (!text || controller.current) return null;
      if (!isOnline()) {
        setOnline(false);
        setProblem("offline");
        return "offline";
      }
      const abort = new AbortController();
      controller.current = abort;
      setProblem(null);
      setFailedQuestion(null);
      setStreaming({ question: text, inScope: null, segments: [] });
      let outcome: ChatProblem | null | undefined;
      const fail = (next: ChatProblem) => {
        outcome = next;
        setStreaming(null);
        setProblem(next);
        if (next === "error") setFailedQuestion(text);
      };
      try {
        await api.askChat(id, text, {
          signal: abort.signal,
          onEvent: (event) => {
            if (current.current !== id) return;
            if (event.event === "scope") {
              setStreaming((s) => (s ? { ...s, inScope: event.in_scope } : s));
            } else if (event.event === "segment") {
              setStreaming((s) => {
                if (!s) return s;
                const segments = s.segments.slice();
                segments[event.index] = event.segment;
                return { ...s, segments };
              });
            } else if (event.event === "done") {
              outcome = null;
              setStreaming(null);
              setThread((prev) => {
                if (!prev) return prev;
                const turns = (prev.turns ?? 0) + 1;
                return {
                  ...prev,
                  turns,
                  full: turns >= prev.max_turns,
                  messages: [...(prev.messages ?? []), event.question, event.answer],
                };
              });
            } else if (event.event === "error") {
              fail("error");
            }
          },
        });
        if (current.current !== id) return null;
        // The stream ended with neither an answer nor an error: it was cut off.
        if (outcome === undefined) fail("error");
        return outcome ?? null;
      } catch (err) {
        if (current.current !== id) return null;
        const e = err as AskError;
        if (e.aborted) {
          setStreaming(null);
          return null;
        }
        if (e.status === undefined) {
          const next = isOnline() ? "error" : "offline";
          fail(next);
          return next;
        }
        const next = chatProblemOf(e);
        setLimit(
          next === "limit_meeting" || next === "fair_use_month"
            ? { limit: e.limit ?? null, resetsAt: e.resetsAt ?? null, plan: e.plan }
            : null
        );
        fail(next);
        if (next === "resting") setThread((prev) => (prev ? { ...prev, resting: true } : prev));
        if (next === "full") setThread((prev) => (prev ? { ...prev, full: true } : prev));
        return next;
      } finally {
        if (controller.current === abort) controller.current = null;
      }
    },
    [sessionId],
  );

  const retry = useCallback(() => {
    if (failedQuestion) void ask(failedQuestion);
  }, [ask, failedQuestion]);

  const remove = useCallback(async () => {
    const id = sessionId;
    try {
      await api.deleteChat(id);
    } catch {
      if (current.current === id) setProblem("delete_failed");
      return false;
    }
    if (current.current !== id) return false;
    setProblem(null);
    setFailedQuestion(null);
    setThread((prev) => (prev ? { ...prev, messages: [], turns: 0, full: false } : prev));
    return true;
  }, [sessionId]);

  const load = useCallback(() => {
    setProblem(null);
    setNonce((n) => n + 1);
  }, []);

  const dismiss = useCallback(() => setProblem(null), []);

  return {
    thread,
    loading,
    problem,
    limit,
    failedQuestion,
    streaming,
    online,
    load,
    ask,
    retry,
    remove,
    dismiss,
  };
}
